import React, { useEffect, useMemo, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import logoNew from "../../../assets/logo-new.png";
import logoCollapsed from "../../../assets/logo-collapsed.png";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarSkeleton from "../Common/SidebarSkeleton";
import { useTheme } from "../../ThemeProvider/ThemeContext";

// Values live in ThemeProvider/darkTheme.css (--sb-*) so light/dark swap in CSS.
const SIDEBAR_COLORS = {
  surface: "var(--sb-surface)",
  border: "var(--sb-border)",
  text: "var(--sb-text)",
  subText: "var(--sb-sub-text)",
  muted: "var(--sb-muted)",
  accent: "var(--sb-accent)",
  accentSoft: "var(--sb-accent-soft)",
  accentBorder: "var(--sb-accent-border)",
  iconActiveBg: "var(--sb-icon-active-bg)",
  chipBg: "var(--sb-chip-bg)",
  chipShadow: "var(--sb-chip-shadow)",
  activeGlow: "var(--sb-active-glow)",
};

const SIDEBAR_TRANSITION_MS = 220;
const SIDEBAR_WIDTH = { collapsed: 82, expanded: 300 };
// Total horizontal inset is 10px, split so the scroller keeps 6px of its own
// padding: active-item borders/glow get room instead of being clipped at its edge.
const SIDEBAR_PADDING_X = 4;
const SIDEBAR_SCROLL_PADDING_X = 6;
// Hover intent: a short open delay so passing the cursor over the rail doesn't
// flash the panel, and a grace period on leave so brief overshoots don't close it.
const HOVER_OPEN_DELAY_MS = 120;
const HOVER_CLOSE_DELAY_MS = 180;
const SIDEBAR_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const Sidebar = ({ tabs, tabsLoaded, isCollapsed, setIsCollapsed }) => {
  const [expandedTab, setExpandedTab] = useState("");
  // isCollapsed is the pinned state. When collapsed, hovering the rail floats
  // the full panel over the page without moving the layout underneath it.
  const [hoverOpen, setHoverOpen] = useState(false);
  const hoverTimer = useRef(null);
  const isOpen = !isCollapsed || hoverOpen;
  const isFloating = isCollapsed && hoverOpen;
  const compact = !isOpen;
  // Content is laid out at its final width immediately; the panel's width
  // transition just reveals/clips it, so labels never wait or reflow mid-animation.
  const panelWidth = isOpen ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed;
  const slotWidth = isCollapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded;
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const visibleTabs = useMemo(() => tabs.filter((tab) => !tab.hidden), [tabs]);
  const currentRoute = `${location.pathname}${location.search}`;
  const currentPath = location.pathname.toLowerCase();
  const labelStyle = {
    flex: 1,
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.25,
  };

  const normalizeIconClass = (icon = "") =>
    icon
      .split(" ")
      .filter(
        (token) =>
          ![
            "fa-solid",
            "fas",
            "far",
            "fa-lg",
            "fa-xl",
            "fa-md",
            "p-2",
            "m-2",
            "px-1",
          ].includes(token),
      )
      .join(" ");

  const renderNavGlyph = (icon, glyphStyle) => (
    <i
      className={`fa-solid ${normalizeIconClass(icon)}`}
      style={glyphStyle}
    />
  );

  const clearHoverTimer = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const scheduleHover = (open) => {
    clearHoverTimer();
    if (!isCollapsed) return;
    hoverTimer.current = setTimeout(
      () => setHoverOpen(open),
      open ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS,
    );
  };

  useEffect(() => clearHoverTimer, []);

  const toggleCollapse = () => {
    clearHoverTimer();
    setHoverOpen(false);
    setIsCollapsed(!isCollapsed);
  };

  const openPath = (path, event = null) => {
    if (event && (event.ctrlKey || event.metaKey)) {
      window.open(path, "_blank");
      return;
    }
    navigate(path);
  };

  const doesSubTabMatchRoute = (subTab) =>
    subTab.path === currentRoute || subTab.legacyPath === currentRoute;

  const isTabActive = (tab) => {
    if (tab.subTabs.length > 0) {
      return (
        currentPath === tab.path.toLowerCase() ||
        tab.subTabs.some((subTab) => doesSubTabMatchRoute(subTab))
      );
    }
    return currentPath === tab.path.toLowerCase();
  };

  const isSubTabActive = (subTab) => doesSubTabMatchRoute(subTab);

  useEffect(() => {
    const activeParent = visibleTabs.find(
      (tab) =>
        tab.subTabs.length > 0 &&
        (currentPath === tab.path.toLowerCase() ||
          tab.subTabs.some(
            (subTab) =>
              subTab.path === currentRoute || subTab.legacyPath === currentRoute,
          )),
    );
    if (activeParent) {
      setExpandedTab(activeParent.name);
    }
  }, [currentPath, currentRoute, visibleTabs]);

  // No tooltips needed: hovering the rail expands it to show full labels.
  const maybeWrapWithTooltip = (content, _text, _placement, key) => (
    <React.Fragment key={key}>{content}</React.Fragment>
  );

  const handleNavigation = (tab, subTab = null, event = null) => {
    if (subTab) {
      openPath(subTab.path, event);
      return;
    }

    if (tab.subTabs.length === 0) {
      openPath(tab.path, event);
      return;
    }

    setExpandedTab((current) => (current === tab.name ? "" : tab.name));
  };

  const renderSidebarButton = ({ icon, label, onClick }) => (
    <button
        type="button"
        className="btn rounded-circle border-0"
        title={label}
        aria-label={label}
        onClick={onClick}
        style={{
          width: "36px",
          height: "36px",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: SIDEBAR_COLORS.chipBg,
          color: SIDEBAR_COLORS.muted,
          boxShadow: SIDEBAR_COLORS.chipShadow,
        }}
      >
        <i className={`fas ${icon} fa-md`} />
      </button>
  );

  const renderSidebarTab = (tab) => {
    const active = isTabActive(tab);

    return maybeWrapWithTooltip(
      <button
        type="button"
        className={`d-flex w-100 border-0 rounded-4 sidebar-item${active ? " is-active" : ""}`}
        style={{
          alignItems: "center",
          justifyContent: compact ? "center" : "flex-start",
          background: active ? SIDEBAR_COLORS.accentSoft : "transparent",
          border: active
            ? `1px solid ${SIDEBAR_COLORS.accentBorder}`
            : "1px solid transparent",
          boxShadow: active ? SIDEBAR_COLORS.activeGlow : "none",
          color: SIDEBAR_COLORS.text,
          minHeight: "52px",
          padding: compact ? 0 : "10px 12px",
          cursor: "pointer",
          transition: "background-color 0.2s ease, border-color 0.2s ease",
          textAlign: "left",
        }}
        onClick={(e) => handleNavigation(tab, null, e)}
      >
        <div
          className={compact ? "" : "me-2"}
          style={{
            width: "36px",
            height: "36px",
            flex: "0 0 36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: active ? SIDEBAR_COLORS.accent : SIDEBAR_COLORS.muted,
            background: active ? SIDEBAR_COLORS.iconActiveBg : "transparent",
            borderRadius: "10px",
          }}
        >
          {renderNavGlyph(tab.icon, {
            fontSize: "1rem",
            lineHeight: 1,
          })}
        </div>
        {!compact && (
          <div
            className="sidebar-label"
            style={{ ...labelStyle, fontWeight: active ? 600 : 500 }}
          >
            {tab.name}
          </div>
        )}
      </button>,
      tab.name,
      "right",
      `tab-${tab.name}`,
    );
  };

  const renderSidebarSubTab = (tab, subTab) => {
    const active = isSubTabActive(subTab);

    return (
      <button
        key={subTab.name}
        type="button"
        className={`d-flex w-100 border-0 rounded-4 sidebar-item${active ? " is-active" : ""}`}
        style={{
          alignItems: "center",
          background: active ? SIDEBAR_COLORS.chipBg : "transparent",
          border: active
            ? `1px solid ${SIDEBAR_COLORS.border}`
            : "1px solid transparent",
          color: SIDEBAR_COLORS.text,
          minHeight: "42px",
          padding: "7px 10px",
          cursor: "pointer",
          textAlign: "left",
          marginTop: "4px",
        }}
        onClick={(e) => handleNavigation(tab, subTab, e)}
      >
        <div
          className="me-2"
          style={{
            width: "34px",
            height: "34px",
            flex: "0 0 34px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: active ? SIDEBAR_COLORS.accent : SIDEBAR_COLORS.muted,
            background: active ? SIDEBAR_COLORS.iconActiveBg : "transparent",
            borderRadius: "10px",
          }}
        >
          {renderNavGlyph(subTab.icon, {
            fontSize: "0.92rem",
            lineHeight: 1,
          })}
        </div>
        <div
          className="sidebar-label"
          style={{ ...labelStyle, fontSize: "0.80rem", color: SIDEBAR_COLORS.subText }}
        >
          {subTab.name}
        </div>
      </button>
    );
  };

  const renderFooterAction = ({ icon, label, path }) => {
    const active = currentPath === path.toLowerCase();

    return maybeWrapWithTooltip(
      <button
        type="button"
        className={`d-flex w-100 border-0 rounded-4 sidebar-item${active ? " is-active" : ""}`}
        style={{
          alignItems: "center",
          justifyContent: compact ? "center" : "flex-start",
          background: active ? SIDEBAR_COLORS.chipBg : "transparent",
          border: active
            ? `1px solid ${SIDEBAR_COLORS.border}`
            : "1px solid transparent",
          color: SIDEBAR_COLORS.text,
          minHeight: "44px",
          padding: compact ? 0 : "8px 12px",
          textAlign: "left",
        }}
        onClick={(event) => openPath(path, event)}
      >
        <div
          className={compact ? "" : "me-2"}
          style={{
            width: "36px",
            flex: "0 0 36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: active ? SIDEBAR_COLORS.accent : SIDEBAR_COLORS.muted,
          }}
        >
          <i className={`fas ${icon} fa-md`} />
        </div>
        {!compact && (
          <div className="sidebar-label" style={{ ...labelStyle, fontWeight: 500 }}>
            {label}
          </div>
        )}
      </button>,
      label,
      "right",
      `footer-${path}`,
    );
  };

  const renderThemeToggle = () => {
    const label = isDark ? "Dark mode" : "Light mode";
    const themeSwitch = (
      <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label="Dark mode"
        className="theme-switch"
        onClick={toggleTheme}
      >
        <span className="theme-switch__thumb">
          <i className={`fas ${isDark ? "fa-moon" : "fa-sun"}`} />
        </span>
      </button>
    );

    if (compact) {
      return maybeWrapWithTooltip(
        <div className="d-flex justify-content-center" style={{ minHeight: "44px", alignItems: "center" }}>
          {themeSwitch}
        </div>,
        isDark ? "Switch to light mode" : "Switch to dark mode",
        "right",
        "footer-theme",
      );
    }

    return (
      <div
        className="d-flex w-100 rounded-4"
        style={{
          alignItems: "center",
          minHeight: "44px",
          padding: "8px 12px",
          color: SIDEBAR_COLORS.text,
        }}
      >
        <div
          className="me-2"
          style={{
            width: "36px",
            flex: "0 0 36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isDark ? SIDEBAR_COLORS.accent : SIDEBAR_COLORS.muted,
          }}
        >
          <i className={`fas ${isDark ? "fa-moon" : "fa-sun"} fa-md`} />
        </div>
        <div className="sidebar-label" style={{ ...labelStyle, fontWeight: 500 }}>
          {label}
        </div>
        {themeSwitch}
      </div>
    );
  };

  return (
    <>
      {tabsLoaded ? (
        <>
          {" "}
          <div
            className="sidebar-slot"
            style={{
              position: "relative",
              height: "100%",
              width: `${slotWidth}px`,
              transition: `width ${SIDEBAR_TRANSITION_MS}ms ${SIDEBAR_EASE}`,
            }}
          >
          <nav
            className={`d-flex flex-column sidebar ${
              compact ? "sidebar-collapsed" : ""
            }${isFloating ? " sidebar-floating" : ""}`}
            onMouseEnter={() => scheduleHover(true)}
            onMouseLeave={() => scheduleHover(false)}
            onFocus={() => scheduleHover(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                scheduleHover(false);
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: isFloating ? 1040 : 1,
              width: `${panelWidth}px`,
              overflow: "hidden",
              transition: `width ${SIDEBAR_TRANSITION_MS}ms ${SIDEBAR_EASE}, box-shadow ${SIDEBAR_TRANSITION_MS}ms ease, border-radius ${SIDEBAR_TRANSITION_MS}ms ease`,
              willChange: "width",
              contain: "layout paint",
              background: SIDEBAR_COLORS.surface,
              borderRight: `1px solid ${SIDEBAR_COLORS.border}`,
              borderRadius: isFloating ? "0 14px 14px 0" : 0,
              padding: `0 ${SIDEBAR_PADDING_X}px`,
            }}
          >
            <div
              className="d-flex flex-column sidebar-scroll"
              style={{
                width: `${panelWidth - SIDEBAR_PADDING_X * 2}px`,
                flex: "0 0 auto",
                height: "100%",
                overflowX: "hidden",
                overflowY: "auto",
                padding: `12px ${SIDEBAR_SCROLL_PADDING_X}px 14px`,
              }}
            >
              {/* Logo */}
              <div
                className="mb-3 mt-1 d-flex justify-content-center align-items-center"
                style={{ minHeight: "58px" }}
              >
                <img
                  key={compact ? "logo-compact" : "logo-full"}
                  src={compact ? logoCollapsed : logoNew}
                  style={{
                    height: "50px",
                    width: compact ? "40px" : "190px",
                    objectFit: "contain",
                  }}
                  alt="EMAlpha Logo"
                  className="img-fluid sidebar-label sidebar-logo"
                />
              </div>
  
              {/* Collapse Toggle */}
              <div
                className={`d-flex mb-3 ${
                  compact ? "justify-content-center" : "justify-content-end"
                }`}
              >
                {renderSidebarButton({
                  icon: isCollapsed
                    ? hoverOpen
                      ? "fa-thumbtack"
                      : "fa-angle-right"
                    : "fa-angle-left",
                  label: isCollapsed ? "Keep sidebar open" : "Collapse sidebar",
                  onClick: toggleCollapse,
                })}
              </div>
  
              {/* Tabs */}
              <div
                className="d-flex flex-column flex-grow-1"
                style={{ gap: "6px" }}
              >
                {visibleTabs.map((tab) => (
                  <React.Fragment key={tab.name}>
                    {renderSidebarTab(tab)}
                    {tab.subTabs.length > 0 &&
                      !compact &&
                      (expandedTab === tab.name || isTabActive(tab)) && (
                      <div
                      className="ps-2 sidebar-label"
                      style={{ marginTop: "2px", marginBottom: "6px" }}
                    >
                        {tab.subTabs.map((subTab) =>
                          renderSidebarSubTab(tab, subTab),
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
  
              {/* Footer */}
              <div
                className="sidebar-footer d-flex flex-column gap-1 mt-auto pt-3"
                style={{ borderTop: `1px solid ${SIDEBAR_COLORS.border}` }}
              >
                {renderThemeToggle()}
                {renderFooterAction({
                  icon: "fa-question-circle",
                  label: "FAQ",
                  path: "/faq",
                })}
                {renderFooterAction({
                  icon: "fa-user",
                  label: "Profile",
                  path: "/profile",
                })}
                {renderFooterAction({
                  icon: "fa-sign-out-alt",
                  label: "Logout",
                  path: "/logout",
                })}
              </div>
            </div>
          </nav>
          </div>
        </>
      ) : (
        <>
          <SidebarSkeleton isCollapsed={isCollapsed} />
        </>
      )}
    </>
  );
};

export default Sidebar;
